import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const useDateFormats = () => {
    const { t } = useTranslation('common');

    return useMemo(() => {
        const emptyValue = t('common:empty.noValue', { defaultValue: '-' });
        const safeFormat = (date: string | Date | Dayjs, formatter: (value: Dayjs) => string) => {
            if (date === null || date === undefined || date === '') {
                return emptyValue;
            }

            const parsed = dayjs(date);
            if (!parsed.isValid()) {
                return emptyValue;
            }

            return formatter(parsed);
        };

        return {
            formatDateTime: (date: string | Date | Dayjs) =>
                safeFormat(date, parsed => parsed.format(t('common:formats.dateTime'))),
            formatDate: (date: string | Date | Dayjs) =>
                safeFormat(date, parsed => parsed.format(t('common:formats.dateFull'))),
            formatLongDate: (date: string | Date | Dayjs) =>
                safeFormat(date, parsed => parsed.format(t('common:formats.longDateFull'))),
            formatDateFullWithTime: (date: string | Date | Dayjs) =>
                safeFormat(date, parsed => parsed.format(t('common:formats.dateFullWithTime'))),
            formatDateYear: (date: string | Date | Dayjs) =>
                safeFormat(date, parsed => parsed.format(t('common:formats.dateYear'))),
            formatDateTimeWithSeconds: (date: string | Date | Dayjs) => {
                if (date === null || date === undefined || date === '') {
                    return emptyValue;
                }

                const parsed = dayjs(date);
                if (!parsed.isValid()) {
                    return emptyValue;
                }

                const formatted = parsed.format(t('common:formats.dateTimeWithSeconds'));
                const [datePart, timePart] = formatted.split('\n');
                return React.createElement(
                    'div',
                    { className: 'text-left' },
                    React.createElement('div', null, datePart),
                    React.createElement('div', { className: 'text-sm text-gray-500' }, timePart)
                );
            },
            formatDateFromTo: (startDate: string | Date | Dayjs, endDate: string | Date | Dayjs) => {
                const start = dayjs(startDate);
                const end = dayjs(endDate);

                if (!start.isValid() || !end.isValid()) {
                    return emptyValue;
                }

                if (start.year() === end.year()) {
                    return `${start.format(t('common:formats.dateMonthDay'))} - ${end.format(t('common:formats.dateFull'))}`;
                }
                return `${start.format(t('common:formats.dateFull'))} - ${end.format(t('common:formats.dateFull'))}`;
            },
            formatWeekMonthYear: (date: string | Date | Dayjs) =>
                safeFormat(date, parsed => parsed.format(t('common:formats.weekMonthYear'))),
            formatMonthYear: (date: string | Date | Dayjs) =>
                safeFormat(date, parsed => parsed.format(t('common:formats.monthYear'))),
        };
    }, [t]);
};

export default useDateFormats;
